import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { message } from "antd";
import { ENROLL_API, PORTAL_EXAM_DETAIL_API, SLOTS_API, START_EXAM } from "../../apis/apis";
import {
  JOURNEY_STEPS,
  MONTH_ABBR,
  MONTH_NAMES,
  STATUS_META,
  bookingText,
  decodeCode,
  downloadTranscript,
  fmtNum,
  formatDate,
  getErr,
  useCountdown,
} from "../../components/portalUtils";
import { ErrorBox, Loader } from "../../components/PortalBits";

const EXAM_URL = "https://exams.hacktonomy.com/";
const pad = (n) => String(n).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

/* ---------------- Stepper ---------------- */
function Stepper({ step, failed }) {
  return (
    <div className="stepper-card">
      <h2>Your certification journey</h2>
      <div className="stepper">
        {JOURNEY_STEPS.map((label, i) => {
          const n = i + 1;
          let cls = "step";
          let dot = n;
          if (n < step) {
            cls += " done";
            dot = "✓";
          } else if (n === step) {
            cls += failed ? " failed" : " current";
            dot = failed ? "!" : n;
          }
          return (
            <div key={label} className={cls}>
              <div className="step-dot">{dot}</div>
              <div className="step-label">{label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Slot picker (compact calendar) ---------------- */
function SlotPicker({ slots, loading, onConfirm, onCancel, busy, heading, badge }) {
  const byDate = useMemo(() => {
    const map = {};
    slots.forEach((s) => {
      if (!s?.date) return;
      (map[String(s.date).slice(0, 10)] ||= []).push(s);
    });
    return map;
  }, [slots]);
  // a slot can be chosen when it has a free seat and is not the current booking
  const free = (s) => !s.full && !s.mine;

  const today = new Date();
  const todayKey = ymd(today.getFullYear(), today.getMonth(), today.getDate());
  const firstFuture =
    Object.keys(byDate).sort().find((k) => k >= todayKey && byDate[k].some(free)) ||
    Object.keys(byDate).sort().find((k) => k >= todayKey);
  const initial = firstFuture ? firstFuture.split("-").map(Number) : [today.getFullYear(), today.getMonth() + 1];
  const [view, setView] = useState({ year: initial[0], month: initial[1] - 1 });
  const [selDate, setSelDate] = useState(null);
  const [selSlot, setSelSlot] = useState(null);

  useEffect(() => {
    if (firstFuture) {
      const [y, m] = firstFuture.split("-").map(Number);
      setView({ year: y, month: m - 1 });
    }
  }, [firstFuture]);

  const shift = (delta) => {
    setSelDate(null);
    setSelSlot(null);
    setView(({ year, month }) => {
      const m = month + delta;
      return { year: year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 };
    });
  };

  const { year, month } = view;
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = firstDow - 1; i >= 0; i--) cells.push({ key: `p${i}`, label: daysInPrev - i, cls: "cbook-day muted" });
  for (let d = 1; d <= daysInMonth; d++) {
    const key = ymd(year, month, d);
    const past = key < todayKey;
    const has = !!byDate[key]?.length;
    const open = has && byDate[key].some(free);
    let cls = "cbook-day";
    let title;
    if (past) cls += " disabled";
    else if (open) cls += " available has-slot";
    else if (has) {
      cls += " disabled full";
      title = "Fully booked";
    } else cls += " muted";
    if (selDate === key) cls += " selected";
    cells.push({ key, label: d, cls, clickable: !past && open, title });
  }
  const trailing = (7 - ((firstDow + daysInMonth) % 7)) % 7;
  for (let i = 1; i <= trailing; i++) cells.push({ key: `n${i}`, label: i, cls: "cbook-day muted" });

  const times = selDate ? byDate[selDate] || [] : [];
  const selLabel = selDate ? (() => {
    const [y, m, d] = selDate.split("-").map(Number);
    return `${MONTH_NAMES[m - 1]} ${d}, ${y}`;
  })() : null;

  return (
    <>
      <div className="cbook-head">
        <h2>{heading}</h2>
        <div className="badge-soft open">{badge}</div>
      </div>
      {!loading && slots.length > 0 && (
        <div className="cbook-legend">
          <span><i className="lg available" /> Available</span>
          <span><i className="lg full" /> Fully booked</span>
          <span><i className="lg past" /> Past dates cannot be chosen</span>
        </div>
      )}
      {loading ? (
        <Loader label="Loading available slots…" />
      ) : slots.length === 0 ? (
        <div className="cert-gate">There are currently no exam slots open for booking. Please check back later or contact Hacktonomy Admin.</div>
      ) : (
        <>
          <div className="cbook-grid">
            <div>
              <div className="cbook-cal-nav">
                <span className="cal-label">{MONTH_NAMES[month]} {year}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button type="button" aria-label="Previous month" onClick={() => shift(-1)}>‹</button>
                  <button type="button" aria-label="Next month" onClick={() => shift(1)}>›</button>
                </div>
              </div>
              <div className="cbook-grid-cal">
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <div key={d} className="cbook-dow">{d}</div>)}
                {cells.map((c) => (
                  <div
                    key={c.key}
                    className={c.cls}
                    onClick={c.clickable ? () => { setSelDate(c.key); setSelSlot(null); } : undefined}
                    role={c.clickable ? "button" : undefined}
                    tabIndex={c.clickable ? 0 : undefined}
                    onKeyDown={c.clickable ? (e) => e.key === "Enter" && (setSelDate(c.key), setSelSlot(null)) : undefined}
                    title={c.title}
                    aria-disabled={!c.clickable || undefined}
                  >
                    {c.label}
                    {c.title && <span className="cbook-full-mark">Full</span>}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="cbook-times-label">
                TIMES{selDate ? ` · ${Number(selDate.slice(8))} ${MONTH_ABBR[Number(selDate.slice(5, 7)) - 1]} ${selDate.slice(0, 4)}` : ""}
              </div>
              {!selDate ? (
                <div className="cbook-empty">Select a date to see available times</div>
              ) : (
                times.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`cbook-slot${selSlot?.id === s.id ? " selected" : ""}`}
                    disabled={!free(s)}
                    title={s.mine ? "Your current slot" : s.full ? "Fully booked" : undefined}
                    onClick={() => free(s) && setSelSlot(s)}
                  >
                    {s.time}
                    {s.mine ? " · your slot" : s.full ? " · Fully booked" : ""}
                  </button>
                ))
              )}
            </div>
          </div>
          <div className="cbook-foot">
            <div className="cbook-selected-text">
              {selDate && selSlot ? (
                <>Selected: <b>{selLabel} · {selSlot.time}</b></>
              ) : selDate ? (
                `Now choose a time for ${selLabel}`
              ) : (
                "No date or time selected yet"
              )}
            </div>
            <div>
              {onCancel && <button type="button" className="cbook-cancel" onClick={onCancel}>Cancel</button>}
              <button type="button" className="btn-cbook" disabled={!selSlot || busy} onClick={() => onConfirm(selSlot)}>
                {busy ? "Booking…" : "Confirm booking"}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

/* ---------------- Confirmed booking ---------------- */
function BookingConfirmed({ exam, onChangeSlot }) {
  const b = exam.booking;
  const cd = useCountdown(b.datetime);
  let pill = null;
  const used = b.reschedules_used ?? b.reschedule_count;
  if (exam.reattempt_pending) {
    // re-attempt given by the admin after a failed result
    const lastTerminated = exam.latest_attempt?.state === "terminated";
    pill = lastTerminated
      ? "NEW SLOT ASSIGNED BY ADMIN"
      : "RE-ATTEMPT  ·  ASSIGNED BY ADMIN";
  } else if (used >= 1) {
    pill = `RESCHEDULED ${used}x`;
    if (b.last_rescheduled_by === "admin") pill += "  ·  RESCHEDULED BY ADMIN";
  } else if (b.assigned_by_admin) {
    pill = "ASSIGNED BY ADMIN";
  }
  const exhausted = b.reschedules_remaining <= 0;
  const blockedNote =
    b.reschedule_message ||
    (exhausted ? "You've used both of your reschedules. Contact Hacktonomy Admin for further changes." : null);
  return (
    <div className="cbook-confirmed show">
      <div className="sched-status">
        <span className="ico">✓</span>
        <span className="lbl">{exam.status === "expired" ? "Slot passed" : "Scheduled"}</span>
      </div>
      <div className="sched-title">
        {exam.full_name} <span className="sched-code">({exam.exam_code})</span>
      </div>
      <div className="sched-datetime">{bookingText(b)}</div>
      {pill && <div className="sched-pill">{pill}</div>}

      {exam.status === "scheduled" && (
        <>
          <div className="sched-countdown-label">Time left until your exam</div>
          <div className="sched-countdown-grid">
            {[["Days", cd.days], ["Hours", cd.hours], ["Minutes", cd.minutes], ["Seconds", cd.seconds]].map(([l, v]) => (
              <div key={l} className="sched-cbox">
                <div className="sched-cval">{v}</div>
                <div className="sched-clabel">{l}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {exam.status === "scheduled" && exam.attempts.length === 0 && (
        <div className="sched-foot">
          <button
            type="button"
            className="cbook-link-btn"
            disabled={!b.can_reschedule}
            title={!b.can_reschedule ? blockedNote || undefined : undefined}
            onClick={b.can_reschedule ? onChangeSlot : undefined}
          >
            Change slot
          </button>
          <span className="sched-limit-note">
            {!b.can_reschedule && blockedNote
              ? blockedNote
              : `${b.reschedules_remaining} reschedule(s) remaining.`}
          </span>
        </div>
      )}
      {exam.status === "expired" && (
        <div className="sched-foot">
          <span className="sched-limit-note">Your booked slot has passed without an attempt. Please contact Hacktonomy Admin to reschedule.</span>
        </div>
      )}
    </div>
  );
}

/* ---------------- Timer / Take exam ---------------- */
function TimerCard({ exam, onTake, starting }) {
  const cd = useCountdown(exam.start_now ? null : exam.booking.window_opens_at);
  const resume = exam.status === "in-progress";
  // 20-minute start buffer after the slot time (client rule)
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const slotMs = exam.booking.datetime ? new Date(exam.booking.datetime).getTime() : null;
  const startByMs = exam.booking.start_by ? new Date(exam.booking.start_by).getTime() : null;
  const inBuffer = !resume && slotMs !== null && startByMs !== null && nowMs >= slotMs && nowMs < startByMs;
  const leftMs = startByMs !== null ? Math.max(0, startByMs - nowMs) : 0;
  const mmss = `${String(Math.floor(leftMs / 60000)).padStart(2, "0")}:${String(Math.floor((leftMs % 60000) / 1000)).padStart(2, "0")}`;
  return (
    <div className="timer-card">
      <div>
        <h3>{resume ? "Your exam is in progress" : exam.start_now ? "Your exam window is open" : "Get ready — your exam window is opening"}</h3>
        <div className="sub">
          {resume
            ? "Continue in the Hacktonomy secure exam browser."
            : exam.start_now
              ? `Your exam is booked for ${bookingText(exam.booking)}.`
              : `Your exam opens ${bookingText(exam.booking)}. Time remaining until it becomes available:`}
        </div>
        {inBuffer && (
          <div className="sub" style={{ marginTop: 8, color: "var(--danger)", fontWeight: 600 }}>
            {Math.ceil(leftMs / 60000)} minutes left to start your examination ({mmss}). If you do not start the
            examination within the remaining time, your attempt will be marked as Invalid.
          </div>
        )}
      </div>
      <div style={{ textAlign: "right" }}>
        {!exam.start_now && <div className="timer-display">{cd.hms}</div>}
        <button type="button" className="btn btn-primary" disabled={!exam.start_now || starting} onClick={onTake} style={{ marginTop: 10 }}>
          {starting ? "Opening…" : resume ? "Resume exam" : "Take exam"}
        </button>
      </div>
    </div>
  );
}

/* ---------------- Result ---------------- */
function ResultCard({ exam, user, onCertify }) {
  const a = exam.latest_attempt;
  if (!a) return null;
  const r = a.result;
  const review = a.state === "in-review";
  const terminated = a.state === "terminated";
  const pass = a.state === "passed";
  const badge = review
    ? ["review", "In review"]
    : terminated
      ? ["fail", a.invalid ? "Invalid" : "Ended"]
      : pass
        ? ["pass", "Pass"]
        : ["fail", "Fail"];
  const left = exam.remaining_attempts;

  return (
    <div className="result-card">
      <div className="result-head">
        <div>
          <div className="code">{exam.exam_code}</div>
          <h3>{exam.full_name}</h3>
        </div>
        <div className={`status-badge ${badge[0]}`}>{badge[1]}</div>
      </div>
      {r ? (
        <div className="result-body">
          <div className="rstat"><div className="label">Total marks</div><div className="value">{fmtNum(r.maximum_score)}</div></div>
          <div className="rstat"><div className="label">Marks gained</div><div className="value">{fmtNum(r.score)}</div></div>
          <div className="rstat"><div className="label">Percentage</div><div className={`value ${pass ? "pass-text" : "fail-text"}`}>{fmtNum(r.percentage)}%</div></div>
          <div className="rstat"><div className="label">Passing score</div><div className="value">{fmtNum(r.passing_percentage ?? exam.passing_percentage)}%</div></div>
        </div>
      ) : (
        <div className="alert info" style={{ margin: 0, borderRadius: 0 }}>
          {terminated
            ? a.invalid
              ? "This attempt is marked as Invalid because the examination was not started within 20 minutes of the scheduled time. Contact Hacktonomy Admin."
              : "This attempt was ended before completion. Contact Hacktonomy Admin if you believe this is an error."
            : a.provisional_pass
              ? "You Have been Provisionally Passed. Final Results will be announced after reviewing attempt"
              : "Your attempt has been submitted. Your result is being reviewed and will be published here once the review is complete."}
        </div>
      )}
      {r && r.domain_performance?.length > 0 && (
        <div className="domain-list">
          {r.domain_performance.map((d, i) => {
            const pct = Math.max(0, Math.min(100, Number(d.percentage) || 0));
            return (
              <div key={`${d.domain_name}-${i}`} className="domain-row">
                <span>{d.domain_name || "—"}</span>
                <span style={{ textAlign: "right", color: "var(--ink-soft)" }}>{fmtNum(d.percentage)}%</span>
                <div className="domain-bar"><span className={pct >= exam.passing_percentage ? "ok" : ""} style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
        </div>
      )}
      {(a.state === "failed" || terminated) && (
        <div className="fail-note">
          {a.state === "failed" ? `Score is below the ${exam.passing_percentage}% passing requirement. ` : ""}
          {/* The number of attempts left is not shown to candidates (client rule). */}
          {left > 0
            ? "Contact Hacktonomy Admin for your next attempt."
            : exam.no_attempt_message || "No attempt is left for this exam. Contact Hacktonomy Admin if you think this is wrong."}
        </div>
      )}
      <div className="result-foot">
        <span>
          Candidate: <b style={{ color: "var(--ink)", textTransform: "capitalize" }}>{user?.name}</b>
          {r?.verification_id ? <span className="verification-line"> · Verification ID {r.verification_id}</span> : null}
        </span>
        {pass && (
          <div className="actions">
            {exam.transcript_enabled && (
              <button type="button" className="btn btn-outline" onClick={() => downloadTranscript({ user, exam, attempt: a })}>
                Download transcript
              </button>
            )}
            {exam.certificate_enabled && (
              <button type="button" className="btn btn-primary" onClick={onCertify}>
                {exam.certificate.status === "approved" ? "View certificate" : "Proceed to certification"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= PAGE ================= */
function CandidateDashboard() {
  const { code } = useParams();
  const examCode = decodeCode(code);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useOutletContext() || {};

  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [rebooking, setRebooking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);

  const fromExplore = location.state?.from === "/user-panel/explore-certifications";

  const loadExam = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await PORTAL_EXAM_DETAIL_API(examCode);
      if (res?.status === false) throw new Error(res?.message);
      setExam(res.data);
    } catch (err) {
      setError(getErr(err, "Unable to load this examination."));
    } finally {
      setLoading(false);
    }
  }, [examCode]);

  const loadSlots = useCallback(async () => {
    setSlotsLoading(true);
    try {
      const fd = new FormData();
      fd.append("exam_code", examCode);
      const res = await SLOTS_API(fd);
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setSlots(list);
    } catch {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [examCode]);

  useEffect(() => {
    loadExam();
  }, [loadExam]);

  const needsPicker = exam && (rebooking || (!exam.booking.confirmed && exam.booking.can_book));
  useEffect(() => {
    if (needsPicker) loadSlots();
  }, [needsPicker, loadSlots]);

  // Keep the page live while the exam window approaches / an attempt is running.
  useEffect(() => {
    if (!exam || !["scheduled", "in-progress", "in-review"].includes(exam.status)) return undefined;
    const id = setInterval(() => loadExam(true), 60000);
    return () => clearInterval(id);
  }, [exam, loadExam]);

  const confirmBooking = async (slot) => {
    if (!slot?.id) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("slot_id", slot.id);
      fd.append("exam_code", examCode);
      await ENROLL_API(fd);
      message.success(exam.booking.confirmed ? "Exam slot rescheduled successfully" : "Exam slot booked successfully");
      setRebooking(false);
      await loadExam(true);
    } catch (err) {
      message.error(getErr(err, "Unable to book the slot"));
      loadSlots();
    } finally {
      setBusy(false);
    }
  };

  const takeExam = async () => {
    setStarting(true);
    try {
      const fd = new FormData();
      fd.append("exam_code", examCode);
      const res = await START_EXAM(fd);
      if (res?.data?.status) {
        const token = res?.data?.data?.access_token;
        const url = token ? `${EXAM_URL}?token=${encodeURIComponent(token)}` : EXAM_URL;
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        message.error(res?.data?.message || "Unable to start exam");
      }
    } catch (err) {
      message.error(getErr(err, "Unable to start exam"));
    } finally {
      setStarting(false);
    }
  };

  const goBack = () => navigate(fromExplore ? "/user-panel/explore-certifications" : "/user-panel");

  if (loading) return <Loader />;
  if (error || !exam) {
    return (
      <>
        <button type="button" className="back-link" onClick={goBack}>← Back to {fromExplore ? "Explore our certifications" : "My exams"}</button>
        <ErrorBox message={error || "Examination not found."} onRetry={() => loadExam()} />
      </>
    );
  }

  const meta = STATUS_META[exam.status] || STATUS_META.available;
  // Once the booked slot has been used for an attempt, the result card takes over.
  // A re-attempt booking is shown even though an earlier (failed) attempt exists.
  const showBookingCard =
    (exam.booking.confirmed && (exam.attempts.length === 0 || exam.reattempt_pending)) || exam.booking.can_book || rebooking;
  // The take-exam card appears once the window is open or within the last 24h before it.
  const opensIn = exam.booking.window_opens_at ? new Date(exam.booking.window_opens_at).getTime() - Date.now() : null;
  const showTimer =
    exam.booking.confirmed &&
    !rebooking &&
    (exam.status === "in-progress" || (exam.status === "scheduled" && (exam.start_now || (opensIn !== null && opensIn <= 86400000))));

  return (
    <>
      <button type="button" className="back-link" onClick={goBack}>
        ← Back to {fromExplore ? "Explore our certifications" : "My exams"}
      </button>

      <Stepper step={rebooking ? 2 : exam.journey_step} failed={exam.journey_failed} />

      <div className="enroll-card">
        <div className="enroll-left">
          <div className="code">{exam.exam_code}</div>
          <h3>{exam.full_name}</h3>
          <div className="enroll-meta">
            {exam.duration_minutes ? <span>⏱ {exam.duration_minutes} minutes</span> : null}
            <span>📝 {exam.questions} questions</span>
            <span>✓ {exam.passing_percentage}% to pass</span>
            {exam.enrolled_at ? <span>Enrolled {formatDate(exam.enrolled_at)}</span> : null}
          </div>
        </div>
        <div className="status-pill" style={{ background: meta.fill, color: meta.text }}>{meta.label}</div>
      </div>

      {showBookingCard && (
        <div className="cbook-card">
          {needsPicker ? (
            <SlotPicker
              slots={slots}
              loading={slotsLoading}
              busy={busy}
              onConfirm={confirmBooking}
              onCancel={rebooking ? () => setRebooking(false) : null}
              heading={rebooking ? "Pick a new date and time for your exam." : "Pick a date and time to book your slot."}
              badge={rebooking ? "Rescheduling" : "Not booked"}
            />
          ) : (
            <BookingConfirmed exam={exam} onChangeSlot={() => setRebooking(true)} />
          )}
        </div>
      )}

      {!showBookingCard && exam.status === "available" && exam.remaining_attempts <= 0 && (
        <div className="cert-gate" style={{ marginBottom: 20 }}>No attempt is left for this exam — contact Hacktonomy Admin.</div>
      )}

      {showTimer && <TimerCard exam={exam} onTake={takeExam} starting={starting} />}

      <ResultCard exam={exam} user={user} onCertify={() => navigate("/user-panel/certification", { state: { exam_code: exam.exam_code } })} />
    </>
  );
}

export default CandidateDashboard;
